import React from 'react';
import { useTranslation } from 'react-i18next';
import { Sun, Moon, Monitor } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useTheme } from '@/lib/theme/ThemeProvider';
import { ThemeMode, type ThemeModeValue } from '@/lib/theme/themeConstants';

interface ThemeOption {
  mode: ThemeModeValue;
  labelKey: string;
  icon: React.ReactNode;
}

const THEME_OPTIONS: ThemeOption[] = [
  { mode: ThemeMode.Light, labelKey: 'header.theme.light', icon: <Sun size={14} aria-hidden="true" /> },
  { mode: ThemeMode.Dark,  labelKey: 'header.theme.dark',  icon: <Moon size={14} aria-hidden="true" /> },
  { mode: ThemeMode.System, labelKey: 'header.theme.system', icon: <Monitor size={14} aria-hidden="true" /> },
];

export function ThemeIcon({ theme, size = 18 }: { theme: ThemeModeValue; size?: number }) {
  if (theme === ThemeMode.Light) return <Sun size={size} aria-hidden="true" />;
  if (theme === ThemeMode.Dark) return <Moon size={size} aria-hidden="true" />;
  return <Monitor size={size} aria-hidden="true" />;
}

/**
 * Tema seçeneklerinin kendisi — hem bağımsız ThemeToggle hem de kullanıcı
 * menüsündeki alt menü tarafından kullanılır.
 */
export function ThemeMenuItems() {
  const { t } = useTranslation();
  const { theme, setTheme } = useTheme();

  const onValueChange = (value: string) => {
    const option = THEME_OPTIONS.find((o) => o.mode === value);
    if (option) setTheme(option.mode);
  };

  return (
    <DropdownMenuRadioGroup value={theme} onValueChange={onValueChange}>
      {THEME_OPTIONS.map(({ mode, labelKey, icon }) => (
        <DropdownMenuRadioItem
          key={mode}
          value={mode}
          indicatorPosition="end"
          className="gap-2"
        >
          {icon}
          <span className="flex-1">{t(labelKey)}</span>
        </DropdownMenuRadioItem>
      ))}
    </DropdownMenuRadioGroup>
  );
}

export function ThemeToggle() {
  const { t } = useTranslation();
  const { theme } = useTheme();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={t('header.theme.toggle.aria')}
          className={cn(
            'inline-flex items-center justify-center rounded-lg p-1.5 transition-[background-color,color,transform] duration-150 ease-out-strong motion-safe:active:scale-[0.94]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring border',
            'bg-transparent border-border text-muted-foreground hover:bg-accent'
          )}
        >
          <ThemeIcon theme={theme} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-32">
        <DropdownMenuLabel>{t('header.theme.label')}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ThemeMenuItems />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
